import React, { useId, useState } from "react";
import { Box, Popover, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { SECTION_ORDER, containerLabel, formatMoney, formatWhole } from "./groupChargesByContainer";
import { RATE_COLORS, unavailableList } from "./ContainerRatesCell";

const C = {
  ...RATE_COLORS,
  brand: "#EE9B1E",
  rowBg: "#FFFBF1",
  sectionText: "#1E8E4E",
  blText: "#8A4E05",
  blBg: "#FFF4E2",
  equipText: "#6B7280",
  equipBg: "#F1F2F4",
  naBorder: "#E2CFA6",
  naBg: "#FFFDF8",
  border: "#E5E7EB",
};

const ALL = "__all__";

const focusRing = {
  "&:focus-visible": { outline: `2px solid ${C.price}`, outlineOffset: "2px" },
};

const buttonReset = {
  font: "inherit",
  margin: 0,
  border: 0,
  background: "none",
  cursor: "pointer",
  textAlign: "left",
};

// Same leg styling as the offer card's Charge Breakdown in OceanMainRateCom.
const SECTION_CFG = {
  Origin: { title: "Origin Charges", tag: "#1F9254", bg: "#E7F6EC" },
  Freight: { title: "Freight Charges", tag: "#2E6FF2", bg: "#EAF1FF" },
  Destination: { title: "Destination Charges", tag: "#1F9254", bg: "#E7F6EC" },
};

// ── Pieces ──

const BasisTag = ({ basis }) => {
  const isBl = basis === "B/L";
  return (
    <Box
      component="span"
      sx={{
        fontSize: 9.5,
        fontWeight: 600,
        lineHeight: 1.5,
        px: "6px",
        borderRadius: "4px",
        color: isBl ? C.blText : C.equipText,
        bgcolor: isBl ? C.blBg : C.equipBg,
        whiteSpace: "nowrap",
      }}
    >
      {basis}
    </Box>
  );
};

const ChargeRow = ({ line, first }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 1.5,
      px: 1.5,
      py: 0.875,
      borderTop: first ? "none" : `1px solid ${C.divider}`,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 500, color: C.text, lineHeight: 1.35 }}>
        {line.name}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, mt: 0.375 }}>
        <Typography component="span" sx={{ fontSize: 10.5, color: C.muted, whiteSpace: "nowrap" }}>
          {line.qty} @ {formatMoney(line.rate)} {line.rateCurrency}
        </Typography>
        <BasisTag basis={line.basis} />
      </Box>
    </Box>
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text, whiteSpace: "nowrap" }}>
      {formatMoney(line.amount)} {line.rateCurrency}
    </Typography>
  </Box>
);

// Coloured "Required · Origin Charges · subtotal" bar. With onToggle it becomes
// an accordion header button with an arrow.
const SectionHeader = ({ section, subtotal, currency, open, onToggle, panelId }) => {
  const cfg = SECTION_CFG[section] || SECTION_CFG.Freight;
  const toggleProps = onToggle
    ? { component: "button", type: "button", "aria-expanded": open, "aria-controls": panelId, onClick: onToggle }
    : {};
  return (
    <Box
      {...toggleProps}
      sx={{
        ...(onToggle ? { ...buttonReset, ...focusRing } : {}),
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 0.75,
        bgcolor: cfg.bg,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        <Box sx={{ bgcolor: "#EFA603", borderRadius: "4px", px: "6px", py: "1px", flexShrink: 0 }}>
          <Typography sx={{ fontSize: 9.5, color: "#fff", fontWeight: 700 }}>Required</Typography>
        </Box>
        <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 700, color: cfg.tag }}>
          {cfg.title}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: cfg.tag, whiteSpace: "nowrap" }}>
          {formatMoney(subtotal)} {currency}
        </Typography>
        {onToggle && (
          <KeyboardArrowDownIcon
            aria-hidden
            sx={{
              fontSize: 18,
              color: cfg.tag,
              transition: "transform 150ms",
              transform: open ? "rotate(180deg)" : "none",
            }}
          />
        )}
      </Box>
    </Box>
  );
};

const sectionBoxSx = (section) => ({
  flexShrink: 0,
  border: `1.5px solid ${(SECTION_CFG[section] || SECTION_CFG.Freight).tag}`,
  borderRadius: "8px",
  overflow: "hidden",
  bgcolor: "#fff",
});

// Single-container view (chip selected): container header + its sections,
// always fully open.
const ContainerGroupCard = ({ group, currency }) => (
    <Box
      sx={{
        flexShrink: 0,
        border: `1px solid ${C.divider}`,
        borderRadius: "10px",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: C.rowBg,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
          <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: C.text, whiteSpace: "nowrap" }}>
            {containerLabel(group)}
          </Typography>
          {group.weightKg > 0 && (
            <Typography component="span" noWrap sx={{ fontSize: 10.5, color: C.muted }}>
              {formatWhole(group.weightKg)} kg
            </Typography>
          )}
        </Box>
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: C.price, whiteSpace: "nowrap" }}>
          {formatMoney(group.total)} {currency}
        </Typography>
      </Box>

      <Box
        sx={{
          borderTop: `1px solid ${C.divider}`,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          p: 1,
        }}
      >
        {group.sections.map((s) => (
          <Box key={s.section} sx={sectionBoxSx(s.section)}>
            <SectionHeader section={s.section} subtotal={s.subtotal} currency={currency} />
            {s.lines.map((l, i) => (
              <ChargeRow key={`${s.section}-${i}`} line={l} first={i === 0} />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
);

// Orange outlined container chip, e.g. "4 × 20GP".
const ContainerChip = ({ group }) => (
  <Box
    component="span"
    sx={{
      fontSize: 11,
      fontWeight: 700,
      lineHeight: 1.4,
      px: 1,
      py: "2px",
      borderRadius: "999px",
      border: `1px solid ${C.brand}`,
      bgcolor: C.blBg,
      color: C.price,
      whiteSpace: "nowrap",
    }}
  >
    {containerLabel(group)}
  </Box>
);

// "All" view, inside a section card: one collapsible row per container;
// opening it lists that container's charge lines for the section.
const ContainerAccordionRow = ({ item, open, onToggle, currency, panelId }) => {
  const { group, lines, subtotal } = item;
  let hasBl = false;
  let hasEquipment = false;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].basis === "B/L") hasBl = true;
    else hasEquipment = true;
  }
  return (
    <Box sx={{ borderTop: `1px solid ${C.divider}` }}>
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
        sx={{
          ...buttonReset,
          ...focusRing,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          px: 1,
          py: 0.875,
          bgcolor: open ? C.rowBg : "#fff",
          "&:hover": { bgcolor: C.rowBg },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, flexWrap: "wrap" }}>
          <KeyboardArrowDownIcon
            aria-hidden
            sx={{
              fontSize: 18,
              color: C.secondary,
              transition: "transform 150ms",
              transform: open ? "none" : "rotate(-90deg)",
            }}
          />
          <ContainerChip group={group} />
          {hasEquipment && <BasisTag basis="Equipment" />}
          {hasBl && <BasisTag basis="B/L" />}
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text, whiteSpace: "nowrap", flexShrink: 0 }}>
          {formatMoney(subtotal)} {currency}
        </Typography>
      </Box>
      {open && (
        <Box id={panelId} sx={{ borderTop: `1px dashed ${C.divider}`, pl: 2 }}>
          {lines.map((l, i) => (
            <ChargeRow key={i} line={l} first={i === 0} />
          ))}
        </Box>
      )}
    </Box>
  );
};

// Pivots container groups into Origin → Freight → Destination, each holding
// one entry per container that has lines in that section.
const groupBySection = (priced) => {
  const out = [];
  for (let s = 0; s < SECTION_ORDER.length; s++) {
    const section = SECTION_ORDER[s];
    const containers = [];
    let subtotal = 0;
    let lineCount = 0;
    for (let g = 0; g < priced.length; g++) {
      const match = priced[g].sections.find((x) => x.section === section);
      if (!match) continue;
      containers.push({ group: priced[g], lines: match.lines, subtotal: match.subtotal });
      subtotal += match.subtotal;
      lineCount += match.lines.length;
    }
    if (containers.length > 0) out.push({ section, containers, subtotal, lineCount });
  }
  return out;
};

const chipSx = (active) => ({
  ...buttonReset,
  ...focusRing,
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1.4,
  px: 1.25,
  py: 0.375,
  borderRadius: "999px",
  border: `1px solid ${active ? C.brand : C.border}`,
  bgcolor: active ? C.blBg : "#fff",
  color: active ? C.blText : C.secondary,
  whiteSpace: "nowrap",
  "&:hover": { borderColor: C.brand },
});

// ── Body ──

const BreakdownBody = ({ breakdown, total, currency }) => {
  const { priced, unavailable, requestedCount, pricedRequestedCount } = breakdown;
  const idPrefix = useId();
  const [active, setActive] = useState(ALL);
  const sections = groupBySection(priced);
  // Keys: `${section}:${containerKey}`. Container rows start collapsed.
  const [expanded, setExpanded] = useState({});

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  const selectChip = (key) => setActive(key);

  const selected = active === ALL ? null : priced.find((g) => g.key === active);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.5,
          bgcolor: C.brand,
          color: "#fff",
        }}
      >
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "inherit" }}>Charge Breakdown</Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: "inherit", whiteSpace: "nowrap" }}>
          {formatMoney(total)} {currency}
        </Typography>
      </Box>

      {/* Filter chips */}
      {priced.length + unavailable.length > 0 && (
        <Box
          role="toolbar"
          aria-label="Filter by container"
          sx={{
            flexShrink: 0,
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${C.divider}`,
          }}
        >
          <Box
            component="button"
            type="button"
            aria-pressed={active === ALL}
            onClick={() => selectChip(ALL)}
            sx={chipSx(active === ALL)}
          >
            All
          </Box>
          {priced.map((g) => (
            <Box
              key={g.key}
              component="button"
              type="button"
              aria-pressed={active === g.key}
              onClick={() => selectChip(g.key)}
              sx={chipSx(active === g.key)}
            >
              {containerLabel(g)}
            </Box>
          ))}
          {unavailable.map((c) => (
            <Box
              key={c.key}
              component="span"
              title="Rate not available"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1.4,
                px: 1.25,
                py: 0.375,
                borderRadius: "999px",
                border: `1px dashed ${C.naBorder}`,
                color: C.muted,
                textDecoration: "line-through",
                cursor: "not-allowed",
                whiteSpace: "nowrap",
              }}
            >
              {containerLabel(c)}
            </Box>
          ))}
        </Box>
      )}

      {/* Groups. flexShrink: 0 on every group so collapsed headers never get
          squashed when another group is expanded; this box scrolls instead. */}
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          maxHeight: 620,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          p: 1.5,
          scrollbarWidth: "thin",
          scrollbarColor: "#C7CDD6 transparent",
        }}
      >
        {active === ALL
          ? sections.map((s) => {
              const cfg = SECTION_CFG[s.section] || SECTION_CFG.Freight;
              return (
                <Box key={s.section} sx={sectionBoxSx(s.section)}>
                  <SectionHeader section={s.section} subtotal={s.subtotal} currency={currency} />
                  <Typography sx={{ fontSize: 10.5, color: C.muted, px: 1.5, py: 0.5 }}>
                    {s.lineCount} line items
                  </Typography>
                  {s.containers.map((item) => {
                    const rowKey = `${s.section}:${item.group.key}`;
                    return (
                      <ContainerAccordionRow
                        key={item.group.key}
                        item={item}
                        open={!!expanded[rowKey]}
                        onToggle={() => toggle(rowKey)}
                        currency={currency}
                        panelId={`${idPrefix}-${s.section}-${item.group.key}`}
                      />
                    );
                  })}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 0.875,
                      borderTop: `1px solid ${C.divider}`,
                      bgcolor: cfg.bg,
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.text }}>Total</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: cfg.tag, whiteSpace: "nowrap" }}>
                      {formatMoney(s.subtotal)} {currency}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          : selected && <ContainerGroupCard group={selected} currency={currency} />}

        {active === ALL &&
          unavailable.map((c) => (
            <Box
              key={c.key}
              sx={{
                flexShrink: 0,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 1,
                px: 1.5,
                py: 1,
                border: `1px dashed ${C.naBorder}`,
                borderRadius: "10px",
                bgcolor: C.naBg,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
                <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: C.muted, whiteSpace: "nowrap" }}>
                  {containerLabel(c)}
                </Typography>
                {c.weightKg > 0 && (
                  <Typography component="span" noWrap sx={{ fontSize: 10.5, color: C.muted }}>
                    {formatWhole(c.weightKg)} kg
                  </Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: C.error, whiteSpace: "nowrap" }}>
                Rate not available
              </Typography>
            </Box>
          ))}

        {priced.length === 0 && unavailable.length === 0 && (
          <Typography sx={{ fontSize: 12, color: C.muted, textAlign: "center", py: 2 }}>
            No charges available.
          </Typography>
        )}
      </Box>

      {/* Coverage footer */}
      {requestedCount > 0 && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderTop: `1px solid ${C.divider}`,
            bgcolor: "#fff",
          }}
        >
          {unavailable.length > 0 ? (
            <Typography sx={{ fontSize: 11, fontWeight: 500, color: C.error, minWidth: 0 }}>
              ⚠ Total excludes {unavailableList(unavailable)} (no rate)
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 11, fontWeight: 500, color: C.sectionText, minWidth: 0 }}>
              ✓ All containers priced
            </Typography>
          )}
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: C.secondary, whiteSpace: "nowrap", flexShrink: 0 }}>
            {pricedRequestedCount} of {requestedCount} priced
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ── Popover ──

// Hover-opened Charge Breakdown popover.
// - breakdown: result of groupChargesByContainer (or null)
// - total / currency: the offer total from the API, shown as-is (never
//   recomputed here)
// - onPaperMouseEnter / onPaperMouseLeave: keep it open while hovered
const ChargeBreakdownPopover = ({
  open,
  anchorEl,
  onClose,
  onPaperMouseEnter,
  onPaperMouseLeave,
  breakdown,
  total,
  currency,
}) => {
  // New offer → fresh chip / expand state.
  let resetKey = `${total}`;
  if (breakdown) {
    for (let i = 0; i < breakdown.priced.length; i++) resetKey += `|${breakdown.priced[i].key}`;
    for (let i = 0; i < breakdown.unavailable.length; i++) resetKey += `|!${breakdown.unavailable[i].key}`;
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      // Hover popover: don't grab focus, and let the pointer pass through the
      // backdrop; the paper re-enables pointer events below.
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      sx={{ pointerEvents: "none" }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          onMouseEnter: onPaperMouseEnter,
          onMouseLeave: onPaperMouseLeave,
          sx: {
            pointerEvents: "auto",
            width: 400,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 20px 50px rgba(31,36,48,0.3)",
          },
        },
      }}
    >
      {breakdown && (
        <BreakdownBody key={resetKey} breakdown={breakdown} total={total} currency={currency || "USD"} />
      )}
    </Popover>
  );
};

export default ChargeBreakdownPopover;
